import { useEffect, useState } from 'react'
import { Megaphone } from 'lucide-react'
import { useAuth } from '../../hooks/useAuth.jsx'
import AnnouncementModal from './AnnouncementModal.jsx'
import { ANNOUNCEMENT_SEEN_KEY, seenMarkFor } from '../constants/announcement.js'

// ─────────────────────────────────────────────────────────────────────────────
// "What changed" — the post-update notice, on demand.
//
// Update 1's notice used to open itself over whatever the person had just signed in to do, with an
// Acknowledge button in the way. The owner asked for this instead while walking staging on
// 2026-09-16: "lets not have it as a popup immediately. lets create a show changes button and have
// this appear if someone presses it."
//
// THE DOT IS THE WHOLE COMPROMISE, and it is the part worth arguing about. A notice nobody is shown
// is a notice nobody reads, and the point of this one is that four companies' staff find out what
// moved in their timesheets and their bids. So the button carries an unread dot until the person
// opens it once — discoverable, ignorable, and gone for good after one look. It is per PERSON and
// survives sign-out (ANNOUNCEMENT_SEEN_KEY), unlike the old acknowledgement, which was per sign-in:
// a dot that came back every morning would be the popup again wearing a smaller hat.
//
// Renders nothing when signed out. TO REMOVE LATER: delete this file and AnnouncementModal.jsx, and
// drop the <WhatChangedButton /> mount in AppHeader.jsx.
// ─────────────────────────────────────────────────────────────────────────────

export default function WhatChangedButton() {
  const { user } = useAuth()
  const [open, setOpen] = useState(false)
  const [seen, setSeen] = useState(true)   // assume seen until proven otherwise: never flash a dot

  useEffect(() => {
    if (!user) return
    let mark = null
    try {
      mark = localStorage.getItem(ANNOUNCEMENT_SEEN_KEY)
    } catch (e) {
      /* storage unavailable — treat as seen; a dot nobody can dismiss is worse than no dot */
    }
    setSeen(mark === seenMarkFor(user))
  }, [user])

  if (!user) return null

  const show = () => {
    setOpen(true)
    setSeen(true)
    try {
      localStorage.setItem(ANNOUNCEMENT_SEEN_KEY, seenMarkFor(user))
    } catch (e) {
      /* storage may be unavailable — the dot simply comes back next time */
    }
  }

  return (
    <>
      <button
        className="ah-btn"
        onClick={show}
        title="What changed in this update"
        style={{ position: 'relative' }}
      >
        <Megaphone size={15} />
        <span className="ah-btn-label">What changed</span>
        {!seen && (
          <span
            aria-hidden="true"
            style={{
              position: 'absolute',
              top: 2,
              right: 2,
              width: 8,
              height: 8,
              borderRadius: '50%',
              background: '#dc2626',
            }}
          />
        )}
      </button>
      <AnnouncementModal open={open} onClose={() => setOpen(false)} />
    </>
  )
}
